import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { SideNav } from "./components/Navigation";
import Maintenance from "./pages/maintenance";
import Loading from "./../common/components/loading/loading";
import "react-toastify/dist/ReactToastify.css";
import { EnvironmentIndicator } from "./components/Environment";
import { MessageHandler } from "./components/common";
import MarketDataTicker from "./../marketData/marketDataTicker";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import { getMarketsWithAttributes } from "../common/services/markets/marketService";
import { getMarketGroupMarkets } from "../common/services/markets/marketGroupMarketService";
import { getProducts } from "../common/services/products/productService";
import { getUserAccountSubscriptionsByUserAccount } from "../common/services/marketdata/userAccountSubscriptionService";
import { getVenues } from "../common/services/markets/venueService";
import { getTermSchedules } from "../common/services/markets/termScheduleService";
import { getUnapprovedMarkCount } from "../common/services/marketdata/markService";
import { getUnapprovedTradeCount } from "../common/services/marketdata/tradeService";
import { usePermitted } from "../common/components/permissions/permissions";
import { getMarketsWithExternalDataSources } from "../common/services/marketdata/externalDataSourceService";
import "./styles/App.css";
import { getUserAccountPreferences } from "./../common/services/auth/userAccountPreferencesService";
import * as Sentry from "@sentry/react";
import FlexShell from "./components/flexShell"; // New component
import FontAwesome from "react-fontawesome";
import TradingProvider from "../common/services/tradingEngine/tradingProvider";
import { TRADING_HUB_ROLES } from "../common/services/tradingEngine/tradingService";
import {
  ENABLE_TRADING,
  ENABLE_MARKET_DATA,
  ENABLE_ALBERTA_ENERGY_DAILY,
  ENABLE_ALBERTA_ENVIRONMENTAL,
} from "../common/services/app/featureFlags";

const App = ({ handleLogout }) => {
  const flexShellRef = useRef(null);
  const { t } = useTranslation();

  //Markets with Attributes
  const [marketsWithAttributes, setMarketsWithAttributes] = useState(null);
  const [theme, setTheme] = useState("dark");
  const [isFooterCollapsed, setIsFooterCollapsed] = useState(true);
  const location = useLocation();

  const [subscribedMarkets, setSubscribedMarkets] = useState([]);
  const { keycloak, initialized } = useKeycloak();
  const userAccountId =
    initialized && keycloak?.tokenParsed?.OriginatingUserAccountID;

  const canConnectTradingHub = usePermitted(TRADING_HUB_ROLES);

  // Check if ticker should be shown on current route
  const isTickerRoute = useCallback(() => {
    const tickerRoutes = [
      "/",
      "/marketdata",
      "/historicalmarketdata",
      "/historicaltrades",
      "/dashboards",
    ];
    return tickerRoutes.some((route) => {
      if (route === "/") {
        return location.pathname === "/";
      }
      return (
        location.pathname === route || location.pathname.startsWith(route + "/")
      );
    });
  }, [location.pathname]);

  const approveMarks = usePermitted(["api-approve-marks"]);
  const approveTrades = usePermitted(["api-approve-trades"]);

  const queryMarkets = usePermitted(["api-query-markets"]);
  const queryMarketGroups = usePermitted(["api-query-market-groups"]);
  const queryProducts = usePermitted(["api-query-products"]);
  const queryVenues = usePermitted(["api-query-venues"]);
  const queryTermSchedules = usePermitted(["api-query-term-schedules"]);
  const queryUserAccountSubscriptions = usePermitted([
    "api-query-user-account-market-subscriptions",
  ]);
  const queryMarks = usePermitted(["api-query-marks"]);

  const isSubscriber = usePermitted([
    "market-data-subscriber",
    "market-data-provider",
    "operator",
    "administrator",
  ]);

  const isDailyReportSubscriber = usePermitted([
    "energy-daily-subscriber",
    "operator",
    "administrator",
  ]);

  const isDailyReportDataSubscriber = usePermitted([
    "energy-daily-data-subscriber",
    "operator",
    "administrator",
  ]);

  const manageExternalMarkets = usePermitted(["api-manage-external-data"]);

  const isAdmin = usePermitted(["administrator"]);

  const bypassSubscriptions = usePermitted(["administrator", "operator"]);

  const enableMessaging =
    process.env.REACT_APP_ENABLE_SIGNALR_MESSAGING === "true";

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (initialized && queryUserAccountSubscriptions) {
          const userAccountPreferences = await getUserAccountPreferences(
            userAccountId
          );
          const theme = userAccountPreferences.theme ?? "dark";
          setTheme(theme);

          // right after you setTheme(theme)
          const scope = Sentry.getCurrentScope();

          // Persist theme for all future captures
          scope.setExtra("theme", theme);
          scope.setTag("theme", theme);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [userAccountId, initialized, queryUserAccountSubscriptions]);

  //Markets with Attributes
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (initialized && queryMarkets) {
          const result = await getMarketsWithAttributes();
          setMarketsWithAttributes(result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [initialized, queryMarkets]);

  //Markets Group Markets
  const [marketGroupMarkets, setMarketGroupMarkets] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (initialized && queryMarketGroups) {
          const result = await getMarketGroupMarkets();
          setMarketGroupMarkets(result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [initialized, queryMarketGroups]);

  //Products
  const [products, setProducts] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (initialized && queryProducts) {
          const result = await getProducts();
          setProducts(result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [initialized, queryProducts]);

  //User Account Subscriptions By UserAccount
  const [
    userAccountSubscriptionsByUserAccount,
    setUserAccountSubscriptionsByUserAccount,
  ] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (initialized && queryUserAccountSubscriptions) {
          const result = await getUserAccountSubscriptionsByUserAccount(
            userAccountId
          );
          setUserAccountSubscriptionsByUserAccount(result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [initialized, queryUserAccountSubscriptions, userAccountId]);

  //Venues
  const [venues, setVenues] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (initialized && queryVenues) {
          const result = await getVenues();
          setVenues(result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [initialized, queryVenues]);

  //Term Schedules
  const [termSchedules, setTermSchedules] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (initialized && queryTermSchedules) {
          const result = await getTermSchedules();
          setTermSchedules(result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [initialized, queryTermSchedules]);

  //Unapproved Mark Count
  const [unapprovedMarkCount, setUnapprovedMarkCount] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (initialized && approveMarks) {
          const result = await getUnapprovedMarkCount();
          setUnapprovedMarkCount(result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();

    const intervalId = setInterval(() => {
      fetchData();
    }, 300000);

    return () => {
      clearInterval(intervalId); //This is important
    };
  }, [initialized, approveMarks]);

  //Unapproved Trade Count
  const [unapprovedTradeCount, setUnapprovedTradeCount] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (initialized && approveTrades) {
          const result = await getUnapprovedTradeCount();
          setUnapprovedTradeCount(result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();

    const intervalId = setInterval(() => {
      fetchData();
    }, 300000);

    return () => {
      clearInterval(intervalId); //This is important
    };
  }, [initialized, approveTrades]);

  // Reset footer collapsed state when navigating between routes
  useEffect(() => {
    // When navigating to a ticker route, ensure ticker is initially collapsed
    if (isTickerRoute()) {
      setIsFooterCollapsed(true);
    }
  }, [isTickerRoute]);

  const memoizedUserOrganizations = useMemo(() => {
    return (initialized && keycloak?.tokenParsed?.group) || [];
  }, [initialized, keycloak]);

  const calculateSubscribedMarkets = useCallback(
    async ({
      marketsWithAttributes,
      products,
      marketGroupMarkets,
      userAccountSubscriptions,
      userOrganizations,
      contributeOnly = false,
      bypassSubscriptions = false,
      filterForExternalDataSources = false,
    }) => {
      if (!marketsWithAttributes || !products || !marketGroupMarkets) {
        return { subscribedMarkets: [], subscribedProducts: [] };
      }

      if (manageExternalMarkets) {
        let marketsWithExternalDataSources = [];

        try {
          marketsWithExternalDataSources =
            await getMarketsWithExternalDataSources();
        } catch {
          console.log("Unable to query external markets.");
        }

        if (filterForExternalDataSources) {
          marketsWithAttributes = marketsWithAttributes.filter(
            (m) =>
              marketsWithExternalDataSources.length === 0 ||
              marketsWithExternalDataSources.includes(m.marketId)
          );
        }

        marketsWithAttributes = marketsWithAttributes.map((m) => ({
          ...m,
          isExternal: marketsWithExternalDataSources.includes(m.marketId),
        }));
      }

      let markets = marketsWithAttributes.filter(
        (m) => m.marketAttributes.length > 0 && m.active
      );

      let subscribedProducts = [];

      if (!bypassSubscriptions) {
        const filteredSubscriptions = userAccountSubscriptions?.filter(
          (s) =>
            userOrganizations.includes(s.organizationId) &&
            s.active === true &&
            (s.contributor || !contributeOnly)
        );

        const subscribedItems = filteredSubscriptions?.map(
          (s) => s.subscribableId
        );

        const marketGroupMarketIds = marketGroupMarkets
          .filter((m) => subscribedItems.includes(m.marketGroupId))
          .map((x) => x.marketId);

        markets = markets
          .filter(
            (m) =>
              subscribedItems.includes(m.marketId) ||
              marketGroupMarketIds.includes(m.marketId)
          )
          .map((m) => {
            const matchingSub = filteredSubscriptions.find(
              (s) =>
                s.subscribableId === m.marketId ||
                marketGroupMarketIds.includes(m.marketId)
            );
            return {
              ...m,
              contributor: matchingSub ? matchingSub.contributor : null,
            };
          });
      } else {
        marketsWithAttributes = marketsWithAttributes.map((m) => ({
          ...m,
          contributor: true,
        }));
      }

      const activeProducts = products.filter((p) => p.active);
      const productIdsByMarket = markets.map((m) => m.productId);
      subscribedProducts = bypassSubscriptions
        ? activeProducts
        : activeProducts.filter((p) =>
            productIdsByMarket.includes(p.productId)
          );

      return {
        subscribedMarkets: bypassSubscriptions
          ? marketsWithAttributes.filter((m) => m.active)
          : markets,
        subscribedProducts,
      };
    },
    [manageExternalMarkets]
  );

  useEffect(() => {
    const fetchSubscribedMarkets = async () => {
      if (
        !marketsWithAttributes ||
        !marketGroupMarkets ||
        !products ||
        !userAccountSubscriptionsByUserAccount
      )
        return;

      const { subscribedMarkets } = await calculateSubscribedMarkets({
          marketsWithAttributes,
          products,
          marketGroupMarkets,
          userAccountSubscriptions: userAccountSubscriptionsByUserAccount,
          userOrganizations: memoizedUserOrganizations,
          contributeOnly: false,
          bypassSubscriptions,
          filterForExternalDataSources: false,
        });

      // Only update state if actually different
      setSubscribedMarkets((prev) =>
        JSON.stringify(prev) !== JSON.stringify(subscribedMarkets)
          ? subscribedMarkets
          : prev
      );
    };

    fetchSubscribedMarkets();
  }, [
    marketsWithAttributes,
    marketGroupMarkets,
    products,
    userAccountSubscriptionsByUserAccount,
    memoizedUserOrganizations,
    bypassSubscriptions,
    calculateSubscribedMarkets,
  ]);

  /******************************************* */
  /* Theme block; stub for now, but will allow */
  /* for theme switching once tied into        */
  /******************************************* */
  const otherTheme = theme === "dark" ? "light" : "dark";
  document.body.classList.remove(otherTheme + "-theme");
  document.body.classList.add(theme + "-theme");
  document.body.setAttribute("data-bs-theme", theme);
  /******************************************* */

  const handleAddTab = useCallback(
    (tabConfig) => {
      if (flexShellRef.current) {
        flexShellRef.current.addTab(tabConfig);
      } else {
        console.log("flexShellRef.current is null!");
      }
    },
    [flexShellRef]
  );

  const [defaultTabConfig, setDefaultTabConfig] = useState(null);

  useEffect(() => {
    // Wait until permissions and data are ready
    const isReady =
      (isDailyReportSubscriber === true || isDailyReportSubscriber === false) &&
      (isSubscriber === true || isSubscriber === false); //defined;

    if (!isReady) return;

    // Decide what default tab should be
    if (ENABLE_ALBERTA_ENERGY_DAILY && isDailyReportSubscriber === true) {
      setDefaultTabConfig({
        id: "albertaenergydaily",
        name: "Alberta Energy Daily",
        component: "AlbertaPowerReport",
        config: {
          route: "/albertaenergydaily",
        },
      });
    } else if (
      ENABLE_MARKET_DATA &&
      isSubscriber === true &&
      subscribedMarkets &&
      subscribedMarkets.length > 0 &&
      termSchedules
    ) {
      const market = subscribedMarkets[0];
      setDefaultTabConfig({
        id: `marketData${market.marketId}`,
        name: t("MarketData") + " - " + market.name,
        component: "MarketData",
        config: {
          market: market,
          termSchedules: termSchedules,
          route: `/marketData/${market.marketId}`,
        },
      });
    } else if (ENABLE_ALBERTA_ENVIRONMENTAL) {
      setDefaultTabConfig({
        id: "albertaenvironmentaldashboard",
        name: "Dashboard",
        component: "DashboardContainerEnvironmental",
        config: {
          route: "/dashboards/albertaenvironmental",
        },
      });
    } else if (ENABLE_ALBERTA_ENERGY_DAILY) {
      setDefaultTabConfig({
        id: "albertaelectricitydashboard",
        name: "Alberta Electricity Dashboard",
        component: "DashboardContainerElectricity",
        config: {
          route: "/dashboards/albertaelectricity",
        },
      });
    } else {
      setDefaultTabConfig(null);
    }
  }, [
    isSubscriber,
    isDailyReportSubscriber,
    subscribedMarkets,
    termSchedules,
    t,
  ]);

  if (
    location.pathname !== "/maintenance" &&
    (!keycloak.authenticated ||
      (queryMarketGroups && !marketGroupMarkets) ||
      (queryMarkets && !marketsWithAttributes) ||
      (queryProducts && !products) ||
      (queryVenues && !venues) ||
      (queryTermSchedules && !termSchedules))
  ) {
    return (
      <div style={{ height: "100vh" }}>
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <ToastContainer theme={theme} />
      {enableMessaging && <MessageHandler />}
      {ENABLE_TRADING && canConnectTradingHub && <TradingProvider />}
      <EnvironmentIndicator />
      <Routes>
        <Route path="/maintenance" element={<Maintenance />} />
        <Route
          path="*"
          element={
            <div className="app-container">
              <SideNav
                unapprovedMarkCount={unapprovedMarkCount}
                unapprovedTradeCount={unapprovedTradeCount}
                handleLogout={handleLogout}
                onMenuClick={handleAddTab}
                userAccountId={userAccountId}
                subscribedMarkets={subscribedMarkets}
                termSchedules={termSchedules}
                manageExternalMarkets={manageExternalMarkets}
              />
              {/* Main Flex Layout */}
              <div className="main-content">
                <div className="content-area">
                  <FlexShell
                    ref={flexShellRef}
                    userAccountId={userAccountId}
                    theme={theme}
                    setTheme={setTheme}
                    allowData={isDailyReportDataSubscriber}
                    isAdmin={isAdmin}
                    isSubscriber={isSubscriber}
                    venues={venues}
                    termSchedules={termSchedules}
                    defaultTabConfig={defaultTabConfig}
                  />
                </div>
                {queryMarks && (
                  <div
                    className={`footer ${isFooterCollapsed ? "collapsed" : ""}`}
                  >
                    <button
                      className={`footer-toggle ${
                        isFooterCollapsed ? "collapsed" : ""
                      }`}
                      onClick={() => setIsFooterCollapsed(!isFooterCollapsed)}
                      aria-label={
                        isFooterCollapsed ? "Expand ticker" : "Collapse ticker"
                      }
                    >
                      <FontAwesome name="chevron-up" className="fa" />
                      <span>{isFooterCollapsed ? "Show" : "Hide"} Ticker</span>
                    </button>
                    <div className="footer-content">
                      <MarketDataTicker userAccountId={userAccountId} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          }
        ></Route>
      </Routes>
    </div>
  );
};

export default App;
